//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Grid, Typography, useTheme } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'

import { PoolTypeEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolBaseOverviewCTO } from '@shared/sharedTypes'

import { PoolOffchainDataCTO, PoolOffchainDataSchema } from '@shared/PoolOffchainDataCTO'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'

import ButtonModal from '@react/components/ButtonModal'
import ViewField from '@react/components/elements/ViewField'
import { poolOffchainDataFetch } from '@react/fetchers/PoolFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { PoolOffchainCollateralView } from './PoolOffchainCollateralView'
import PoolOffchainDataForm from './PoolOffchainDataForm'

export const PoolOffchainDataView: FC<{ poolAddr: EthAddr }> = ({ poolAddr }): React.ReactElement => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
  } = useOtChainState()
  const [poolOffchainDataCTO, setPoolOffchainDataCTO] = useState<PoolOffchainDataCTO | undefined>(undefined)
  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolBaseOverviewCTO
  )
  const poolType = poolOverviewCTO?.poolType
  const fetchData = () => {
    if (poolAddr && serverApiBaseUrl) {
      logOT.debug('PoolOffchainDataView: poolOffchainDataCTO', poolAddr, serverApiBaseUrl)
      poolOffchainDataFetch(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_poolOffchainDataCTO) => {
          logOT.debug('PoolOffchainDataView: poolOffchainDataCTO', _poolOffchainDataCTO)
          setPoolOffchainDataCTO(_poolOffchainDataCTO)
        })
        .catch((err) => {
          console.error('Error fetching pool offchain data', err)
        })
    }
  }
  useEffect(() => {
    window.addEventListener(
      'modalClose',
      () => {
        logOT.debug('Fetch Data 1 modalClose event')

        fetchData()
      },
      false
    )

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          logOT.debug('Fetch Data 2 modalClose event')

          fetchData()
        },
        false
      )
    }
  }, [])
  const theme = useTheme()

  useEffect(() => {
    logOT.debug('PoolOffchainDataView: poolOffchainDataCTO', poolAddr && serverApiBaseUrl)

    fetchData()
  }, [poolAddr, serverApiBaseUrl])

  const dateUpdatedCallback = () => {
    fetchData()
  }

  return (
    <Box>
      <Box
        sx={{
          height: '50px',
          backgroundColor: theme.palette.background.subtle,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0px',
          gap: '8px',
          paddingLeft: '16px',
          paddingRight: '16px',
          borderRadius: 1,
        }}
      >
        <Typography variant={'bodyBold'}>Pool Offchain Details</Typography>
        {poolOffchainDataCTO && (
          <ButtonModal title={'Edit'} variant="primary">
            <PoolOffchainDataForm poolOffchainDataCTO={poolOffchainDataCTO} />
          </ButtonModal>
        )}
      </Box>
      {poolOffchainDataCTO && (
        <Grid container spacing={4} sx={{ marginTop: 4, paddingLeft: '16px', paddingRight: '16px' }}>
          <Grid item xs={12} sm={12}>
            <ViewField
              dataObject={poolOffchainDataCTO}
              fieldSchema={PoolOffchainDataSchema.describe().fields.poolAddr}
            ></ViewField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolOffchainDataCTO}
              fieldSchema={PoolOffchainDataSchema.describe().fields.timestamp}
            ></ViewField>
          </Grid>
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.benchmarkRate}
              ></ViewField>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolOffchainDataCTO}
              fieldSchema={PoolOffchainDataSchema.describe().fields.advisoryFeeRate}
            ></ViewField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolOffchainDataCTO}
              fieldSchema={PoolOffchainDataSchema.describe().fields.platformFeeRate}
            ></ViewField>
          </Grid>
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.liquidityFeeRate}
              ></ViewField>
            </Grid>
          )}

          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.contractualCurrencty}
              ></ViewField>
            </Grid>
          )}

          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.minimumTransferAmount}
              ></ViewField>
            </Grid>
          )}
          {[PoolTypeEnum.FlexibleRate, PoolTypeEnum.Dynamic].includes(poolType) && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.maximumTransferAmount}
              ></ViewField>
            </Grid>
          )}

          {poolType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.transferInDays}
              ></ViewField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Dynamic && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.transferOutDays}
              ></ViewField>
            </Grid>
          )}

          {poolType === PoolTypeEnum.Term && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.earlyWithdrawalProcessingPeriod}
              ></ViewField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Term && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.automaticRollover}
              ></ViewField>
            </Grid>
          )}
          {poolType === PoolTypeEnum.Term && (
            <Grid item xs={12} sm={6}>
              <ViewField
                dataObject={poolOffchainDataCTO}
                fieldSchema={PoolOffchainDataSchema.describe().fields.rolloverCollateral}
              ></ViewField>
            </Grid>
          )}
        </Grid>
      )}
      {poolOffchainDataCTO && (
        <PoolOffchainCollateralView
          poolOffchainDataCTO={poolOffchainDataCTO}
          dateUpdatedCallback={dateUpdatedCallback}
        />
      )}
    </Box>
  )
}
